import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { XMarkIcon, CheckIcon, UserIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { subscriptionAPI, formatCurrency, formatDateTime, getStatusColor, getStatusLabel, getOfferTypeLabel } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import toast from 'react-hot-toast';

const SubscriptionDetailModal = ({ subscription, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Mutations pour validation et rejet
  const approveMutation = useMutation(
    ({ id, data }) => subscriptionAPI.approveSubscription(id, data),
    {
      onSuccess: () => {
        toast.success('Souscription validée avec succès');
        queryClient.invalidateQueries('admin-subscriptions');
        queryClient.invalidateQueries('subscription-stats');
        setShowApprovalModal(false);
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la validation');
      },
    }
  );

  const rejectMutation = useMutation(
    ({ id, data }) => subscriptionAPI.rejectSubscription(id, data),
    {
      onSuccess: () => {
        toast.success('Souscription rejetée avec succès');
        queryClient.invalidateQueries('admin-subscriptions');
        queryClient.invalidateQueries('subscription-stats');
        setShowRejectionModal(false);
        setRejectionReason('');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors du rejet');
      }
    }
  );

  const handleApprove = () => {
    setShowApprovalModal(true);
  };

  const handleReject = () => {
    setShowRejectionModal(true);
  };

  const confirmApproval = () => {
    if (subscription) {
      approveMutation.mutate({
        id: subscription.id,
        data: {}
      });
    }
  };

  const confirmRejection = () => {
    if (subscription && rejectionReason.trim()) {
      rejectMutation.mutate({
        id: subscription.id,
        data: { rejectionReason: rejectionReason.trim() }
      });
    }
  };

  if (!isOpen || !subscription) return null;

  const isEnAttente = (subscription.status?.libelle || subscription.status) === 'En attente';

  const mutualiste = subscription.mutualiste || {};
  const phoneParts = [];
  if (mutualiste.mobile1) phoneParts.push(mutualiste.mobile1);
  if (mutualiste.mobile2) phoneParts.push(mutualiste.mobile2);
  const phoneDisplay = phoneParts.length > 0 ? phoneParts.join(' / ') : 'Non renseigné';

  return (
    <>
      {/* Modal principal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <DocumentTextIcon className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="text-lg font-medium text-white">
                      Détail de la souscription
                    </h3>
                    <p className="text-primary-100 text-sm">
                      {subscription.offer?.name} - {getOfferTypeLabel(subscription.offer?.type)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-primary-100 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Contenu */}
            <div className="bg-white px-6 py-6 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Informations du mutualiste */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <UserIcon className="h-5 w-5 text-primary-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Informations du mutualiste</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Nom complet</label>
                      <p className="text-gray-900 font-medium">
                        {subscription.mutualiste?.nom} {subscription.mutualiste?.prenoms}
                      </p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Matricule</label>
                      <p className="text-gray-900">{subscription.mutualiste?.matricule}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-gray-900">{subscription.mutualiste?.user?.email}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Téléphone</label>
                      <p className="text-gray-900">{phoneDisplay}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date de naissance</label>
                      <p className="text-gray-900">
                        {mutualiste.dateNaissance 
                          ? new Date(mutualiste.dateNaissance).toLocaleDateString('fr-FR')
                          : 'Non renseignée'
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Détails de la souscription */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Détails de la souscription</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Produit</label>
                      <p className="text-gray-900 font-medium">{subscription.offer?.name}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Type</label>
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {getOfferTypeLabel(subscription.offer?.type)}
                      </span>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Montant</label>
                      <p className="text-gray-900 font-bold text-lg text-green-600">
                        {formatCurrency(subscription.amount)}
                      </p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Nombre de bénéficiaires</label>
                      <p className="text-gray-900">{subscription.beneficiariesCount || 1}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Statut</label>
                      <span className={`badge badge-${getStatusColor(subscription.status?.libelle || subscription.status)}`}>
                        {getStatusLabel(subscription.status?.libelle || subscription.status)}
                      </span>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date de souscription</label>
                      <p className="text-gray-900">{formatDateTime(subscription.createdAt)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Informations supplémentaires si rejetée */}
              {subscription.rejectionReason && (
                <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-red-800 mb-2">Raison du rejet</h4>
                  <p className="text-red-700">{subscription.rejectionReason}</p>
                </div>
              )}
            </div>

            {/* Actions pour les souscriptions en attente */}
            {isEnAttente && (
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleReject}
                    className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <XMarkIcon className="h-4 w-4 mr-2" />
                    Rejeter
                  </button>
                  <button
                    onClick={handleApprove}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Valider
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de validation */}
      {showApprovalModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowApprovalModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <CheckIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Valider la souscription
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Vous êtes sur le point de valider la souscription de{' '}
                        <strong>{subscription.mutualiste?.nom} {subscription.mutualiste?.prenoms}</strong>{' '}
                        pour le produit <strong>{subscription.offer?.name}</strong>.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmApproval}
                  disabled={approveMutation.isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approveMutation.isLoading ? (
                    <LoadingSpinner size="small" className="mr-2" />
                  ) : null}
                  Valider la souscription
                </button>
                <button
                  type="button"
                  onClick={() => setShowApprovalModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de rejet */}
      {showRejectionModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowRejectionModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <XMarkIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Rejeter la souscription
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Vous êtes sur le point de rejeter la souscription de{' '}
                        <strong>{subscription.mutualiste?.nom} {subscription.mutualiste?.prenoms}</strong>{' '}
                        pour le produit <strong>{subscription.offer?.name}</strong>.
                      </p>
                      <div className="mt-4">
                        <label className="form-label">Raison du rejet *</label>
                        <textarea
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          className="form-input"
                          rows="3"
                          placeholder="Expliquez la raison du rejet..."
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmRejection}
                  disabled={rejectMutation.isLoading || !rejectionReason.trim()}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rejectMutation.isLoading ? (
                    <LoadingSpinner size="small" className="mr-2" />
                  ) : null}
                  Rejeter la souscription
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRejectionModal(false);
                    setRejectionReason('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionDetailModal;
